// Building blocks of the analytics screens: figures, bars, the funnel, the daily chart, tables.
import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { isRTL, t } from '@/i18n';
import { colors, radius } from '@/theme';
import { Card, Header, s, T } from '@/ui';
import { formatCell, num, pct, stepName } from './format';
import type { DetailSection } from './api';

export const PERIODS = [7, 30, 90];

export function periodLabel(days: number) {
  return t(`آخر ${days} يوم`, `Last ${days} days`, `دوایین ${days} ڕۆژ`);
}

/** Header with a back arrow and a title (the sub-screens). */
export function TopBar({ title, onBack, children }: { title: string; onBack?: () => void; children?: React.ReactNode }) {
  return (
    <Header>
      <View style={[s.row, { gap: 10 }]}>
        {onBack ? (
          <Pressable onPress={onBack} hitSlop={12} accessibilityLabel={t('رجوع', 'Back')}>
            <Ionicons name={isRTL() ? 'arrow-forward' : 'arrow-back'} size={22} color={colors.white} />
          </Pressable>
        ) : null}
        <T w="black" size={18} color={colors.white} style={{ flex: 1 }}>{title}</T>
      </View>
      {children}
    </Header>
  );
}

/** 7 / 30 / 90 days, on the gradient header. */
export function PeriodPicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <View style={[s.row, { gap: 8, marginTop: 12 }]}>
      {PERIODS.map((d) => (
        <Pressable key={d} onPress={() => onChange(d)} style={[st.period, days === d && st.periodOn]} accessibilityRole="button" accessibilityState={{ selected: days === d }}>
          <T w="bold" size={12} color={days === d ? colors.violet : colors.white}>{periodLabel(d)}</T>
        </Pressable>
      ))}
    </View>
  );
}

/** A card that folds: tap its title to open or close it (`extra` stays in the title row). */
export function Section({ title, hint, children, extra, defaultOpen = false }: {
  title: string; hint?: string; children: React.ReactNode; extra?: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card style={{ marginTop: 12, paddingVertical: open ? 14 : 10 }}>
      <View style={[s.between, { alignItems: 'center', gap: 8 }]}>
        <Pressable onPress={() => setOpen((o) => !o)} style={[s.row, { flex: 1, gap: 8 }]} accessibilityRole="button" accessibilityState={{ expanded: open }} hitSlop={6}>
          <Ionicons name={open ? 'chevron-down' : isRTL() ? 'chevron-back' : 'chevron-forward'} size={18} color={colors.violet} />
          <T w="black" size={15} style={{ flex: 1 }}>{title}</T>
        </Pressable>
        {extra}
      </View>
      {open ? (
        <View style={{ marginTop: 6 }}>
          {hint ? <T size={12} color={colors.muted} style={{ marginBottom: 6 }}>{hint}</T> : null}
          {children}
        </View>
      ) : null}
    </Card>
  );
}

/**
 * A long list in a box of its own that scrolls inside the page: the first `max` rows, then
 * "show more" adds the rest (still inside the box).
 */
export function ScrollList<Row>({ rows, max = 10, height = 320, render }: { rows: Row[]; max?: number; height?: number; render: (row: Row, i: number) => React.ReactNode }) {
  const [all, setAll] = useState(false);
  if (!rows.length) return <T size={13} color={colors.muted}>{t('لا توجد بيانات بعد.', 'No data yet.')}</T>;
  const shown = all ? rows : rows.slice(0, max);
  return (
    <View>
      <ScrollView nestedScrollEnabled style={{ maxHeight: height }} contentContainerStyle={{ gap: 8 }} showsVerticalScrollIndicator>
        {shown.map(render)}
      </ScrollView>
      {rows.length > max ? <ShowMore all={all} rest={rows.length - max} onToggle={() => setAll((a) => !a)} /> : null}
    </View>
  );
}

/** "Show N more" / "Show less" under a long list. */
export function ShowMore({ all, rest, onToggle }: { all: boolean; rest: number; onToggle: () => void }) {
  return (
    <Pressable onPress={onToggle} hitSlop={6} style={[s.row, { gap: 4, alignSelf: 'flex-start', marginTop: 8 }]} accessibilityRole="button">
      <Ionicons name={all ? 'chevron-up' : 'chevron-down'} size={14} color={colors.violet} />
      <T w="bold" size={12} color={colors.violet}>{all ? t('عرض أقل', 'Show less') : t(`عرض ${num(rest)} غيرها`, `Show ${num(rest)} more`)}</T>
    </Pressable>
  );
}

/** A figure; tapping it opens its details. */
export function Kpi({ label, value, sub, onPress }: { label: string; value: string; sub?: string | null; onPress?: () => void }) {
  return (
    <Pressable onPress={onPress} disabled={!onPress} style={({ pressed }) => [st.kpi, pressed && { opacity: 0.8 }]} accessibilityRole={onPress ? 'button' : undefined}>
      <View style={s.between}>
        <T size={12} color={colors.muted} style={{ flex: 1 }}>{label}</T>
        {onPress ? <Ionicons name={isRTL() ? 'chevron-back' : 'chevron-forward'} size={14} color={colors.violet} /> : null}
      </View>
      <T w="black" size={18}>{value}</T>
      {sub ? <T size={11} color={colors.muted}>{sub}</T> : null}
    </Pressable>
  );
}

/** Horizontal bars: [{label, value}], the longest = full width, with the share of the total. */
export type BarRow = { label: string; value: number; key?: string };

/** Horizontal bars; with `onPress(row)` each row opens its details (e.g. one platform's visitors). */
export function Bars({ rows, unit, onPress }: { rows: BarRow[]; unit?: string; onPress?: (row: BarRow) => void }) {
  const u = unit ?? t('زيارة', 'visits');
  const max = Math.max(...rows.map((r) => r.value), 1);
  const total = rows.reduce((a, r) => a + r.value, 0);
  if (!rows.length || total === 0) return <T size={13} color={colors.muted}>{t('لا توجد بيانات بعد.', 'No data yet.')}</T>;
  return (
    <View style={{ gap: 8 }}>
      {rows.map((r, i) => (
        <Pressable
          key={`${r.label}-${i}`}
          onPress={onPress ? () => onPress(r) : undefined}
          disabled={!onPress}
          accessibilityRole={onPress ? 'button' : undefined}
          style={({ pressed }) => (onPress ? { opacity: pressed ? 0.7 : 1 } : null)}
        >
          <View style={[s.between, { gap: 8 }]}>
            <View style={[s.row, { flex: 1, gap: 4 }]}>
              <T size={13} style={{ flexShrink: 1 }} numberOfLines={1} color={onPress ? colors.violet : colors.text}>{r.label}</T>
              {onPress ? <Ionicons name="chevron-back-outline" size={14} color={colors.violet} /> : null}
            </View>
            <T size={12} color={colors.muted}>{`${num(r.value)} ${u} · ${pct(r.value, total)}%`}</T>
          </View>
          <View style={st.track}>
            <View style={[st.fill, { width: `${(r.value / max) * 100}%` }]} />
          </View>
        </Pressable>
      ))}
    </View>
  );
}

/** The visit → order steps, each with its share of the step before. */
export function Funnel({ steps }: { steps: { step: string; visits: number }[] }) {
  const first = steps[0]?.visits || 0;
  let worst: { from: string; to: string; kept: number } | null = null;
  steps.forEach((st2, i) => {
    if (i === 0) return;
    const prev = steps[i - 1].visits;
    const kept = prev > 0 ? st2.visits / prev : 1;
    if (prev > 0 && (!worst || kept < worst.kept)) worst = { from: steps[i - 1].step, to: st2.step, kept };
  });
  const w = worst as { from: string; to: string; kept: number } | null;
  return (
    <View style={{ gap: 10 }}>
      {steps.map((st2, i) => {
        const prev = i ? steps[i - 1].visits : null;
        return (
          <View key={st2.step}>
            <View style={[s.between, { gap: 8 }]}>
              <T w="semibold" size={13}>{stepName(st2.step)}</T>
              <T size={12} color={colors.muted}>
                {`${num(st2.visits)} ${t('زيارة', 'visits')}${prev !== null && prev > 0 ? ` · ${pct(st2.visits, prev)}% ${t('من السابقة', 'of the previous')}` : ''}`}
              </T>
            </View>
            <View style={[st.track, { height: 12 }]}>
              <View style={[st.fill, { height: 12, backgroundColor: colors.turquoise, width: `${first ? Math.max((st2.visits / first) * 100, 1) : 0}%` }]} />
            </View>
          </View>
        );
      })}
      {w && first > 0 ? (
        <View style={st.warn}>
          <Ionicons name="warning-outline" size={16} color={colors.warning} />
          <T size={12} color={colors.warning} style={{ flex: 1 }}>
            {t(
              `أكبر خسارة بين «${stepName(w.from)}» و«${stepName(w.to)}»: يكمل ${Math.round(w.kept * 100)}% فقط إلى الخطوة التالية.`,
              `Biggest drop between "${stepName(w.from)}" and "${stepName(w.to)}": only ${Math.round(w.kept * 100)}% go on to the next step.`
            )}
          </T>
        </View>
      ) : null}
    </View>
  );
}

/** Visits and orders per day as two rows of bars (no chart library on the phone). */
export function DailyChart({ daily }: { daily: { date: string; visits: number; orders: number }[] }) {
  if (!daily.length) return <T size={13} color={colors.muted}>{t('لا توجد بيانات بعد.', 'No data yet.')}</T>;
  const maxV = Math.max(...daily.map((d) => d.visits), 1);
  const maxO = Math.max(...daily.map((d) => d.orders), 1);
  const labelEvery = Math.ceil(daily.length / 6);
  const rtl = isRTL();
  // The newest day on the reading side's end, like the dashboard chart.
  const days = rtl ? [...daily].reverse() : daily;
  return (
    <View>
      <View style={[s.row, { gap: 12, marginBottom: 6 }]}>
        <Legend color={colors.violet} label={`${t('الزيارات', 'Visits')} (${t('الأعلى', 'max')} ${num(maxV)})`} />
        <Legend color={colors.turquoise} label={`${t('الطلبات', 'Orders')} (${t('الأعلى', 'max')} ${num(maxO)})`} />
      </View>
      <View style={[st.chart, { flexDirection: 'row' }]}>
        {days.map((d) => (
          <View key={d.date} style={st.day}>
            <View style={[st.bar, { backgroundColor: colors.violet, height: `${(d.visits / maxV) * 100}%` }]} />
            <View style={[st.bar, { backgroundColor: colors.turquoise, height: `${(d.orders / maxO) * 100}%` }]} />
          </View>
        ))}
      </View>
      <View style={{ flexDirection: 'row', marginTop: 4 }}>
        {days.map((d, i) => (
          <View key={d.date} style={{ flex: 1, alignItems: 'center' }}>
            {i % labelEvery === 0 ? <T size={9} color={colors.faint}>{`${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`}</T> : null}
          </View>
        ))}
      </View>
    </View>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <View style={[s.row, { gap: 4 }]}>
      <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: color }} />
      <T size={11} color={colors.muted}>{label}</T>
    </View>
  );
}

/** A small chip of text (summary figures, tags). */
export function Chip({ label, tone = 'grey' }: { label: string; tone?: 'grey' | 'violet' | 'success' | 'danger' | 'warning' | 'turquoise' }) {
  const bg = { grey: colors.greyTint, violet: colors.violetTint, success: colors.successTint, danger: colors.dangerTint, warning: colors.warningTint, turquoise: colors.turquoiseTint }[tone];
  const fg = { grey: colors.text, violet: colors.violetDark, success: colors.success, danger: colors.danger, warning: colors.warning, turquoise: colors.turquoiseDark }[tone];
  return (
    <View style={[s.pill, { backgroundColor: bg, paddingVertical: 4 }]}>
      <T w="bold" size={11} color={fg}>{label}</T>
    </View>
  );
}

/** A table as one card per row: "column: value" lines (phones have no room for columns). */
export function RowCards({ rows, columns }: { rows: Record<string, unknown>[]; columns: { key: string; label: string; format?: string }[] }) {
  const [head, ...rest] = columns;
  return (
    <ScrollList
      rows={rows}
      render={(r, i) => (
        <View key={i} style={st.rowCard}>
          <T w="bold" size={13}>{formatCell(r[head.key], head.format)}</T>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 2 }}>
            {rest.map((c) => (
              <T key={c.key} size={12} color={colors.muted}>
                {`${c.label}: `}
                <T size={12} w="semibold">{formatCell(r[c.key], c.format)}</T>
              </T>
            ))}
          </View>
        </View>
      )}
    />
  );
}

/** One section of a details answer (backend App\Services\AnalyticsDetail). */
export function DetailBlock({ section }: { section: DetailSection }) {
  return (
    <Section title={section.title} defaultOpen>
      {section.type === 'bars' ? <Bars rows={section.rows} unit={section.unit || ''} /> : <RowCards rows={section.rows} columns={section.columns} />}
    </Section>
  );
}

export const st = StyleSheet.create({
  period: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.6)' },
  periodOn: { backgroundColor: colors.white, borderColor: colors.white },
  kpi: { flexGrow: 1, flexBasis: '45%', backgroundColor: colors.card, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, paddingVertical: 10, paddingHorizontal: 12 },
  track: { height: 8, borderRadius: 4, backgroundColor: colors.greyTint, marginTop: 3, overflow: 'hidden' },
  fill: { height: 8, borderRadius: 4, backgroundColor: colors.violet },
  warn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.warningTint, borderRadius: radius.md, padding: 8, marginTop: 4 },
  chart: { height: 140, alignItems: 'flex-end', borderBottomWidth: 1, borderBottomColor: colors.border },
  day: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'center', gap: 1, height: '100%', paddingHorizontal: 1 },
  bar: { flex: 1, borderTopLeftRadius: 2, borderTopRightRadius: 2, minHeight: 1 },
  rowCard: { backgroundColor: colors.bg, borderRadius: radius.md, paddingVertical: 8, paddingHorizontal: 10 },
});
